import React, { useState, useEffect, Fragment, useContext, useRef } from "react";
import { Autocomplete, Button, Checkbox, RadioGroup, FormControlLabel, Radio, Rating, Slider, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography, Box, Alert } from "@mui/material";
import apiService from "../services/api-service";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/it";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import SearchIcon from "@mui/icons-material/Search";

// Importa icone per il marker di Leaflet
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { position } from "stylis";
import { AuthContext } from "../context";
import toast from "react-hot-toast";

// Configurazione dell'icona di default per Leaflet
const defaultIcon = L.icon({
	iconUrl: markerIcon,
	shadowUrl: markerShadow,
	iconSize: [25, 41],
	iconAnchor: [12, 41],
	popupAnchor: [1, -34],
	shadowSize: [41, 41],
});

// Componente per selezione di posizione tramite clic
const LocationMarker = ({ onPositionSelect }) => {
	const [position, setPosition] = useState(null);

	useMapEvents({
		click(e) {
			const newPosition = e.latlng;
			setPosition(newPosition);
			onPositionSelect(newPosition); // Callback per aggiornare il form
		},
	});

	return position ? <Marker position={position} icon={defaultIcon} /> : null; // Usa l'icona definita manualmente
};

// Funzione di ricerca tramite Google Places API

const GenericForm = (props) => {
	const [formData, setFormData] = useState(props?.formData || {});
	const [toggleValue, setToggleValue] = useState(null);
	const [errorFields, setErrorFields] = useState([]);
	const [errorFieldsMessage, setErrorFieldsMessage] = useState([]);
	const [errorMessage, setErrorMessage] = useState("");
	const [showSuccess, setShowSuccess] = useState(false);
	const navigate = useNavigate();

	const [position, setPosition] = useState(
		props?.old_data?.location
			? {
					lat: props?.old_data?.location?.lat,
					lng: props?.old_data?.location?.lng,
				}
			: null
	);

	const handleGoogleSearch = async (query, fieldName) => {
		try {
			const response = await fetch(`https://maps.googleapis.com/maps/api/place/textsearch/json?query=${query}&key=AIzaSyBH__gEjglI0DzkCuq33ioG3_ayXHP4ojo`);
			const data = await response.json();

			if (data.results && data.results.length > 0) {
				const { lat, lng } = data.results[0].geometry.location;
				setPosition({ lat, lng }); // Aggiorna la posizione
				setFormData((prevData) => ({
					...prevData,
					[fieldName]: { lat, lng },
					[fieldName + "_address"]: query,
				}));
			} else {
				alert("Nessun risultato trovato per la ricerca!");
			}
		} catch (error) {
			console.error("Errore durante la ricerca: ", error);
		}
	};

	const [isInitialized, setIsInitialized] = useState(false);

	useEffect(() => {
		if (!isInitialized && props?.old_data && props?.old_data?.location?.lat && props?.old_data?.location?.lng) {
			setFormData(props.old_data);
			setPosition({
				lat: props.old_data?.location?.lat,
				lng: props.old_data?.location?.lng,
			});
			setIsInitialized(true);
		} else if (!isInitialized && props?.old_data) {
			// Converti gli array di ID in oggetti per i campi autocomplete multipli
			const processedData = { ...props.old_data };
			console.log("[GenericForm] Processing old_data:", processedData);
			if (props?.inputFields) {
				props.inputFields.forEach((field) => {
					if (field.type === "autocomplete" && field.multiple && Array.isArray(processedData[field.name])) {
						const value = processedData[field.name];
						console.log(`[GenericForm] Processing field "${field.name}":`, {
							value: value,
							optionsAvailable: field.options?.length || 0
						});
						if (value.length > 0) {
							const firstValue = value[0];
							// Se il primo elemento è un numero/ID, converti tutti gli ID in oggetti
							if (typeof firstValue === 'number' || (typeof firstValue === 'string' && !isNaN(firstValue))) {
								if (field.options && field.options.length > 0) {
									processedData[field.name] = value.map(id => {
										const option = field.options.find(opt => {
											const optId = opt.id;
											const valId = typeof id === 'string' ? parseInt(id) : id;
											return optId === id || optId === valId;
										});
										return option || id;
									}).filter(val => val !== undefined && val !== null);
									console.log(`[GenericForm] Converted "${field.name}" from IDs to objects:`, processedData[field.name]);
								} else {
									console.log(`[GenericForm] Options not available yet for "${field.name}", keeping IDs`);
								}
							}
						}
					}
				});
			}
			setFormData(processedData);
			setIsInitialized(true);
		}
	}, [props.old_data, props?.inputFields, isInitialized]);

	// Inizializza i defaultValue dei campi quando il componente si monta
	// Usa un ref per tracciare se è la prima inizializzazione
	const isFirstRender = useRef(true);
	const hasUserInteracted = useRef(false);
	
	useEffect(() => {
		if (props?.inputFields) {
			setFormData((prevData) => {
				const initialData = { ...prevData };
				let hasChanges = false;
				
				props.inputFields.forEach((field) => {
					// Solo applica defaultValue se è la prima inizializzazione o se il campo non ha ancora un valore
					// Non applicare defaultValue se l'utente ha già interagito con il form
					if (field.defaultValue !== undefined && 
						!hasUserInteracted.current && 
						(isFirstRender.current || initialData[field.name] === undefined)) {
						initialData[field.name] = field.defaultValue;
						hasChanges = true;
					}
				});
				
				if (isFirstRender.current) {
					isFirstRender.current = false;
				}
				
				return hasChanges ? initialData : prevData;
			});
		}
	}, [props?.inputFields]);

	// Riconverti gli ID in oggetti quando le opzioni vengono caricate per i campi autocomplete multipli
	useEffect(() => {
		if (props?.inputFields) {
			setFormData((prevData) => {
				const updatedData = { ...prevData };
				let hasChanges = false;
				
				props.inputFields.forEach((field) => {
					if (field.type === "autocomplete" && field.multiple && field.options && field.options.length > 0) {
						const value = updatedData[field.name];
						if (Array.isArray(value) && value.length > 0) {
							const firstValue = value[0];
							// Se il primo elemento è un numero/ID ma non è già un oggetto con id, convertilo
							if ((typeof firstValue === 'number' || (typeof firstValue === 'string' && !isNaN(firstValue))) && 
								typeof firstValue !== 'object') {
								const converted = value.map(id => {
									const option = field.options.find(opt => {
										const optId = opt.id;
										const valId = typeof id === 'string' ? parseInt(id) : id;
										return optId === id || optId === valId;
									});
									return option || id;
								}).filter(val => val !== undefined && val !== null);
								
								// Verifica se ci sono differenze (almeno un elemento convertito)
								const hasConversion = converted.some((item, idx) => {
									const original = value[idx];
									return typeof item === 'object' && (typeof original === 'number' || typeof original === 'string');
								});
								
								if (hasConversion) {
									updatedData[field.name] = converted;
									hasChanges = true;
									console.log(`[GenericForm] Converted IDs to objects for field "${field.name}":`, {
										original: value,
										converted: converted,
										optionsCount: field.options.length
									});
								}
							}
						}
					}
				});
				
				return hasChanges ? updatedData : prevData;
			});
		}
	}, [props?.inputFields?.map(f => f.name + '-' + (f.options?.length || 0)).join(',')]);

	useEffect(() => {
		if (props?.executeOnChange) {
			props.executeOnChangeTask(formData);
		}
	}, [formData]);

	useEffect(() => {
		if (props.formData) {
			setFormData(props.formData);
		}
	}, [props.formData]);

	// Auto-seleziona il valore quando c'è una sola opzione per un campo autocomplete
	useEffect(() => {
		if (props?.inputFields) {
			props.inputFields.forEach((field) => {
				if (field.type === "autocomplete" && !field.multiple && field.options && field.options.length === 1) {
					// Se il campo non ha ancora un valore e c'è una sola opzione, selezionala automaticamente
					setFormData((prevData) => {
						if (prevData[field.name] === undefined || prevData[field.name] === null) {
							const autoValue = field.valueToSave ? field.valueToSave(field.options[0]) : field.options[0];
							console.log(`[GenericForm] Auto-selecting single option for field "${field.name}":`, {
								option: field.options[0],
								savedValue: autoValue,
								currentFormData: prevData[field.name]
							});
							return {
								...prevData,
								[field.name]: autoValue,
							};
						}
						return prevData;
					});
				}
			});
		}
	}, [props?.inputFields]);

	// Gestisce la modifica dei dati del form
	const handleInputChange = (field, value) => {
		console.log(`[GenericForm] handleInputChange - field: "${field}", value:`, value);
		hasUserInteracted.current = true;
		setFormData((prevData) => ({
			...prevData,
			[field]: value,
		}));
	};

	// Controlla i campi obbligatori
	const validateRequiredFields = () => {
		console.log("[GenericForm] validateRequiredFields - formData:", formData);
		console.log("[GenericForm] validateRequiredFields - requiredFields:", props?.requiredFields);
		
		if (!props?.requiredFields) {
			return true; // Nessun campo obbligatorio
		}
		const missingFields = props?.requiredFields?.filter((field) => {
			const value = formData[field["name"]];
			const isMissing = value === undefined || value === null || value === "";
			console.log(`[GenericForm] Checking field "${field["name"]}":`, {
				value: value,
				type: typeof value,
				isMissing: isMissing
			});
			return isMissing;
		}); // Filtra i campi mancanti

		console.log("[GenericForm] Missing fields:", missingFields);
		setErrorFields(missingFields.map((field) => field["name"]));
		setErrorFieldsMessage(missingFields.map((field) => field["nameDiplayed"]));
		return missingFields.length === 0;
	};

	// Gestione del metodo di salvataggio
	const isEmailValid = (email) => {
		const emailPattern = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/; // RegEx per email valida
		return emailPattern.test(email);
	};

	const handleSave = async () => {
		if (!validateRequiredFields()) return; // Blocca salvataggio se ci sono errori
		// Controllo validità dell'email
		if (formData.login_email && !isEmailValid(formData.login_email)) {
			setErrorMessage("L'email inserita non è valida. Inserisci un'email corretta.");
			setErrorFields(["login_email"]);
			return; // Interrompi il salvataggio
		}

		if (props?.useAPI && props?.url) {
			try {
				await apiService
					.post(
						props.url,
						{
							"Content-Type": "application/json",
						},
						formData
					)

					.then((data) => {
						if (data) {
							if (props?.redirectOnSuccess) {
								navigate(`${props.redirectOnSuccess}/${data["id"]}`);
							} else {
								if (!props?.notChangeId)
									setFormData({
										...formData,
										id: data["id"],
									});
								// setShowSuccess(true);
								toast.success("Dati salvati con successo!", {
									id: "generic-form-success",
								});
								setErrorMessage("");
								if (props?.executeOnSuccess) {
									props.executeOnSuccessTask();
								}
							}
						}
					})
					.catch((error) => {
						console.log("son quaaa");
						console.log("error", typeof error, error.toString());
						if (error && typeof error.toString() === "string")
							return toast.error(error.toString(), {
								id: "generic-form-error",
							});

						console.dir(error, { depth: null });
						if (error && typeof error === "string") {
							console.log("son quaaa 1");
							return toast.error(error, {
								id: "generic-form-error",
							});
						}
						if (error && error.error && typeof error.error === "string") {
							console.log("son quaaa 1");

							return toast.error(error.error, {
								id: "generic-form-error",
							});
						}
						if (props?.showErrorFromAPI) {
							setErrorMessage(error.message);
							return toast.error(error.message, {
								id: "generic-form-error",
							});
						}

						toast.error("Errore durante la chiamata al server, codice d'errore #00006", {
							id: "generic-form-error",
						});
					});
			} catch (error) {
				toast.error("Errore durante il salvataggio dei dati, codice d'errore #00005", {
					id: "generic-form-error",
				});
			}
		} else if (props?.state_callback) {
			props.state_callback(formData);
			setShowSuccess(true);
			// toast.success("Dati salvati con successo!", {
			// 	id: "generic-form-success",
			// });
		}
	};

	const authContext = useContext(AuthContext);

	return authContext.hasAuth(`VIEW_${props?.authModelName}`) || props?.authModelName === undefined ? (
		<Box
			component="form"
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 2,
				padding: 3,
				borderRadius: 2,
				backgroundColor: "#f5f5f5", // Grigio chiaro per il filtro
				boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)", // Ombreggiatura delicata per un effetto leggero
				marginBottom: props.isFilter ? "20px" : "0px", // Margine inferiore solo per i filtri
			}}
		>
			{/* Messaggi di errore e successo */}
			{errorFields.length > 0 && !(errorFields.length === 1 && errorFields.includes("login_email")) && <Alert severity="error">Compila i campi obbligatori: {errorFieldsMessage.join(", ")}</Alert>}
			{errorMessage !== "" && (
				<Alert severity="error">
					{errorMessage}
					<br></br>
					{!props?.showErrorFromAPI && "Se l'errore persiste contatti il supporto tecnico indicato il codice d'errore"}
				</Alert>
			)}
			{showSuccess && <Alert severity="success">Dati salvati con successo!</Alert>}
			{props?.requiredFields?.length > 0 && (
				<Typography variant="caption" color="textSecondary" gutterBottom>
					* campo obbligatorio
				</Typography>
			)}
			{props?.inputFields?.map((field, index) => (
				<Box key={index} sx={{ mb: 2 }}>
					<Typography variant="subtitle2" color="textSecondary" gutterBottom>
						{field.type === "text" ? field.label : field.helperText || field.label}
						{props?.requiredFields?.some((requiredField) => requiredField["name"] === field.name) ? " *" : null}
					</Typography>
					{(() => {
						switch (field.type) {
							case "autocomplete":
								let autocompleteValue = formData[field.name] || (field.multiple ? [] : field.options.length === 1 ? field.options[0] : null);
								
								// Se è multiplo e il valore è un array di ID, convertili in oggetti dalle opzioni
								if (field.multiple && Array.isArray(autocompleteValue) && autocompleteValue.length > 0) {
									const firstValue = autocompleteValue[0];
									// Se il primo elemento è un numero/ID, converti tutti gli ID in oggetti
									if (typeof firstValue === 'number' || (typeof firstValue === 'string' && !isNaN(firstValue))) {
										autocompleteValue = autocompleteValue.map(id => {
											const option = field.options?.find(opt => opt.id === id || opt.id === parseInt(id));
											return option || id;
										}).filter(val => val !== undefined && val !== null);
									}
								}
								
								console.log(`[GenericForm] Rendering autocomplete "${field.name}":`, {
									formDataValue: formData[field.name],
									autocompleteValue: autocompleteValue,
									optionsCount: field.options?.length,
									options: field.options
								});
								return (
									<>
										<Autocomplete
											options={field.options || []}
											key={`${field.name}-${index}`}
											multiple={field.multiple || false}
											disabled={field.disabled || false}
											value={autocompleteValue}
											onChange={(event, value) => {
												console.log(`[GenericForm] Autocomplete "${field.name}" onChange:`, {
													rawValue: value,
													event: event
												});
												const finalValue = field.valueToSave ? field.valueToSave(value) : value;
												console.log(`[GenericForm] Autocomplete "${field.name}" finalValue:`, finalValue);
												handleInputChange(field.name, finalValue);
											}}
											getOptionLabel={(option) => {
												return field.valueToDisplay(option, field.options);
											}}
											isOptionEqualToValue={(option, value) => {
												return field.valueComparator ? field.valueComparator(option, value) : option.id === value.id;
											}}
											getOptionDisabled={
												field.getOptionDisabled
													? (option) => {
															return field.getOptionDisabled(option, formData[field.name]);
														}
													: undefined
											}
											renderInput={(params) => <TextField {...params} variant="outlined" error={errorFields.includes(field.name)} />}
										/>
										{field.advanced &&
											Array.isArray(formData[field.name]) &&
											formData[field.name].length > 0 &&
											formData[field.name].map((item, index) => {
												return (
													<Box
														key={index}
														sx={{
															mt: 2,
															display: "flex",
															flexDirection: "column",
															gap: 2,
															backgroundColor: "#f5f5f5",
															padding: 2,
															borderRadius: 2,
															boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
														}}
													>
														<Typography variant="subtitle2" color="textSecondary" gutterBottom>
															{field.valueToDisplay(item, field.options)}
														</Typography>
														<Box
															sx={{
																display: "flex",
																gap: 2,
																flexDirection: "row",
															}}
														>
															{field.fieldForEachValue?.map((subfield, subindex) => {
																switch (subfield.type) {
																	case "text":
																		return <TextField key={subindex} label={subfield.label} value={formData[`${field.name}_${item}_${subfield.name}`] || ""} onChange={(e) => handleInputChange(`${field.name}_${item}_${subfield.name}`, e.target.value)} error={errorFields.includes(`${field.name}_${item}_${subfield.name}`)} helperText={errorFields.includes(`${field.name}_${item}_${subfield.name}`) ? subfield.helperText : ""} />;
																	case "switch":
																		return <FormControlLabel control={<Switch checked={!!formData[`${field.name}_${item}_${subfield.name}`]} onChange={(e) => handleInputChange(`${field.name}_${item}_${subfield.name}`, e.target.checked)} />} label={subfield.label} />;
																	default:
																		return null;
																}
															})}
														</Box>
													</Box>
												);
											})}
									</>
								);
							case "checkbox":
								return <FormControlLabel control={<Checkbox checked={!!formData[field.name]} onChange={(e) => handleInputChange(field.name, e.target.checked)} required={field.required || false} />} label={field.label} required={field.required || false} />;
							case "map":
								return (
									<div>
										{/* Barra di ricerca */}
										<div
											style={{
												display: "flex",
												flexDirection: "row",
												alignItems: "center",
												justifyContent: "space-between",
												width: "420px",
												marginBottom: "10px",
											}}
										>
											<TextField
												type="text"
												placeholder="Cerca posizione"
												value={formData[field.name + "_address"] || ""}
												onChange={(e) => handleInputChange(field.name + "_address", e.target.value)}
												onKeyPress={(e) => {
													if (e.key === "Enter") handleGoogleSearch(e.target.value, field.name);
												}}
												sx={{ width: "300px" }}
												error={errorFields.includes(field.name)}
											/>
											<Button
												variant="contained"
												color="primary"
												startIcon={<SearchIcon />}
												sx={{
													marginLeft: "10px",
													padding: "6px 16px",
													fontSize: "14px",
													textTransform: "none",
													borderColor: "rimary.light",
													color: "white!important",
													background: "linear-gradient(90deg, #42424a, #000000)",
													":hover": {
														background: "linear-gradient(90deg, #42424a, #000000)",
													},
													":disabled": {
														background: "rgba(0, 0, 0, 0.12)",
														color: "#AAAAAA!important",
														opacity: "0.8!important",
														cursor: "not-allowed",
													},
												}}
												disabled={!formData[field.name + "_address"] || formData[field.name + "_address"] === ""}
												onClick={() => handleGoogleSearch(formData[field.name + "_address"], field.name)}
											>
												Cerca
											</Button>
										</div>

										{/* Mappa */}
										<MapContainer
											key={position ? `${position?.lat},${position?.lng}` : "default"}
											center={position ? [position?.lat, position?.lng] : [45.1563425, 10.7914387]} // Centra su Roma
											zoom={13}
											style={{
												height: "500px",
												width: "100%",
												marginTop: "10px",
											}}
										>
											<TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />

											{/* Marker per la posizione della ricerca */}
											{position && <Marker position={formData[field.name]} icon={defaultIcon} />}

											{/* Marker per selezione posizione tramite clic */}
											<LocationMarker
												onPositionSelect={(position) => {
													handleInputChange(field.name, {
														lat: position.lat,
														lng: position.lng,
													});
												}}
											/>
										</MapContainer>
									</div>
								);
							case "radio":
								return (
									<RadioGroup value={formData[field.name] || ""} onChange={(e) => handleInputChange(field.name, e.target.value)} required={field.required || false}>
										{field.options.map((option, i) => (
											<FormControlLabel key={i} value={option.value} control={<Radio />} label={option.label} required={field.required || false} />
										))}
									</RadioGroup>
								);
							case "adder":
								return (
									<Box>
										<Button
											onClick={() => {
												const newInstance = {};
												field.fieldForEachInstance.forEach((subfield) => {
													newInstance[subfield.name] = subfield.type === "switch" ? true : false;
												});
												handleInputChange(field.name, [...(formData[field.name] || []), newInstance]);
											}}
											sx={{
												mt: 2,
												marginBottom: "30px",
												background: "#000", // Sfondo nero
												color: "#fff!important", // Testo bianco
												fontSize: "1.2rem", // Dimensione del font più grande
												borderRadius: "16px", // Bordo rotondo
												"&:hover": {
													background: "#000", // Sfondo nero
													color: "#fff", // Testo bianco
												},
												"&:active": {
													background: "#000", // Sfondo nero
													color: "#fff", // Testo bianco
												},
												"&:focus": {
													background: "#000", // Sfondo nero anche quando è in focus
													color: "#fff", // Testo bianco in focus
													boxShadow: "none", // Rimuove l'ombra di focus (effetto blu predefinito)
												},
											}}
										>
											+
										</Button>

										{formData[field.name]?.map((instance, idx) => (
											<Box
												key={idx}
												sx={{
													display: "flex",
													flexDirection: "column",
													gap: 2,
													padding: 2,
													border: "1px solid #ccc",
													borderRadius: 2,
													marginBottom: 2,
													position: "relative",
													paddingTop: "45px",
													boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
												}}
											>
												<Button
													sx={{
														position: "absolute",
														top: 4,
														left: 4,
														padding: 0,
														minWidth: "30px",
														minHeight: "30px",
														backgroundColor: "#f44336",
														color: "#fff",
														borderRadius: "50%",
														"&:hover": {
															backgroundColor: "#d32f2f",
														},
													}}
													onClick={() => {
														const updatedInstances = [...formData[field.name]];
														updatedInstances.splice(idx, 1);
														handleInputChange(field.name, updatedInstances);
													}}
												>
													X
												</Button>
												{field.fieldForEachInstance.map((subfield, subIdx) => {
													switch (subfield.type) {
														case "text":
															return (
																<TextField
																	key={subIdx}
																	label={subfield.label}
																	multiline={subfield.multiline || false}
																	rows={subfield.rows || 1}
																	value={instance[subfield.name] || ""}
																	onChange={(e) => {
																		const updatedInstances = [...formData[field.name]];
																		updatedInstances[idx][subfield.name] = e.target.value;
																		handleInputChange(field.name, updatedInstances);
																	}}
																/>
															);
														case "switch":
															return (
																<FormControlLabel
																	key={subIdx}
																	control={
																		<Switch
																			defaultChecked={instance[subfield.name]}
																			onChange={(e) => {
																				const updatedInstances = [...formData[field.name]];
																				updatedInstances[idx][subfield.name] = e.target.checked;
																				handleInputChange(field.name, updatedInstances);
																			}}
																		/>
																	}
																	label={subfield.label}
																/>
															);
														case "autocomplete":
															return (
																<>
																	<Autocomplete
																		options={subfield.options || []}
																		key={`${subfield.name}-${subIdx}`}
																		multiple={subfield.multiple || false}
																		value={formData[field.name][idx][subfield.name] || (subfield.multiple ? [] : subfield.options.length === 1 ? subfield.options[0] : null)}
																		onChange={(event, value) => {
																			const finalValue = subfield.valueToSave ? subfield.valueToSave(value) : value;

																			const updatedInstances = [...formData[field.name]];
																			updatedInstances[idx][subfield.name] = finalValue;
																			handleInputChange(field.name, updatedInstances);
																		}}
																		getOptionLabel={(option) => {
																			return subfield.valueToDisplay(option, subfield.options);
																		}}
																		isOptionEqualToValue={(option, value) => {
																			return subfield.valueComparator ? subfield.valueComparator(option, value) : option.id === value.id;
																		}}
																		renderInput={(params) => <TextField {...params} variant="outlined" label={subfield.label} error={errorFields.includes(subfield.name)} />}
																	/>
																	{subfield.advanced &&
																		Array.isArray(formData[subfield.name]) &&
																		formData[subfield.name].length > 0 &&
																		formData[subfield.name].map((item, index) => {
																			return (
																				<Box
																					key={index}
																					sx={{
																						mt: 2,
																						display: "flex",
																						flexDirection: "column",
																						gap: 2,
																						backgroundColor: "#f5f5f5",
																						padding: 2,
																						borderRadius: 2,
																						boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
																					}}
																				>
																					<Typography variant="subtitle2" color="textSecondary" gutterBottom>
																						{subfield.valueToDisplay(item, subfield.options)}
																					</Typography>
																					<Box
																						sx={{
																							display: "flex",
																							gap: 2,
																							flexDirection: "row",
																						}}
																					>
																						{subfield.fieldForEachValue?.map((subsubfield, subindex) => {
																							switch (subsubfield.type) {
																								case "text":
																									return <TextField key={subindex} label={subsubfield.label} value={formData[`${subfield.name}_${item}_${subsubfield.name}`] || ""} onChange={(e) => handleInputChange(`${subfield.name}_${item}_${subsubfield.name}`, e.target.value)} error={errorFields.includes(`${subfield.name}_${item}_${subsubfield.name}`)} helperText={errorFields.includes(`${subfield.name}_${item}_${subsubfield.name}`) ? subsubfield.helperText : ""} />;
																								case "switch":
																									return <FormControlLabel control={<Switch checked={!!formData[`${subfield.name}_${item}_${subsubfield.name}`]} defaultChecked={true} onChange={(e) => handleInputChange(`${subfield.name}_${item}_${subsubfield.name}`, e.target.checked)} />} label={subsubfield.label} />;
																								default:
																									return null;
																							}
																						})}
																					</Box>
																				</Box>
																			);
																		})}
																</>
															);
														default:
															return null;
													}
												})}
											</Box>
										))}
									</Box>
								);
							case "date":
								return (
									<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
										<DatePicker
											label={field.label}
											defaultValue={field.defaultValue ? dayjs(field.defaultValue) : null}
											value={formData[field.name] ? dayjs(formData[field.name]) : null}
											onChange={(newValue) => {
												const formattedDate = newValue ? dayjs(newValue).format("YYYY-MM-DD") : null;
												handleInputChange(field.name, formattedDate);
											}}
											sx={{
												"& .MuiOutlinedInput-root": {
													"& fieldset": {
														borderColor: errorFields.includes(field.name) ? "red" : "default",
													},
													"&:hover fieldset": {
														borderColor: errorFields.includes(field.name) ? "red" : "default",
													},
													"&.Mui-focused fieldset": {
														borderColor: errorFields.includes(field.name) ? "red" : "default",
													},
												},
											}}
											renderInput={(params) => (
												<TextField
													{...params}
													name={field.name}
													error={errorFields.includes(field.name)}
													onChange={(e) => {
														const parsedDate = dayjs(e.target.value, "DD-MM-YYYY");
														if (parsedDate.isValid()) {
															handleInputChange(field.name, parsedDate.format("DD-MM-YYYY"));
														}
													}}
												/>
											)}
											inputFormat="DD-MM-YYYY"
										/>
									</LocalizationProvider>
								);
							case "rating":
								return <Rating value={formData[field.name] || 0} onChange={(e, value) => handleInputChange(field.name, value)} />;
							case "textarea": // Aggiungi supporto per TextArea
								return (
									<TextField
										multiline
										rows={field?.rows || 2} // Puoi modificare il numero di righe in base alle tue necessità
										value={formData[field.name] || ""}
										onChange={(e) => handleInputChange(field.name, e.target.value)}
										variant="outlined"
										label={field.label} // Assicurati che il campo `label` esista
										fullWidth // Per occupare l'intera larghezza disponibile
									/>
								);
							case "slider":
								return <Slider value={formData[field.name] || 0} onChange={(e, value) => handleInputChange(field.name, value)} aria-labelledby="slider" min={field.min || 0} max={field.max || 100} />;
							case "switch":
								return <FormControlLabel control={<Switch checked={!!formData[field.name]} onChange={(e) => handleInputChange(field.name, e.target.checked)} defaultChecked={true} />} label={field.label} />;
							case "text":
								return (
									<TextField
										value={formData[field.name] || ""}
										onChange={(e) => handleInputChange(field.name, e.target.value)}
										variant="outlined"
										helperText={field.helperText}
										error={errorFields.includes(field.name)}
										required={field.required || false}
										inputProps={
											field.pattern
												? {
														pattern: field.pattern.source,
													}
												: {}
										}
									/>
								);
							case "email":
								return <TextField type="email" value={formData[field.name] || ""} onChange={(e) => handleInputChange(field.name, e.target.value)} variant="outlined" helperText={field.helperText} error={errorFields.includes(field.name)} disabled={field.disabled || false} required={field.required || false} />;
							case "toggle":
								return (
									<ToggleButtonGroup
										value={toggleValue}
										exclusive
										onChange={(e, newValue) => {
											setToggleValue(newValue);
											handleInputChange(field.name, newValue);
										}}
									>
										{field.options.map((option, i) => (
											<ToggleButton key={i} value={option.value}>
												{option.label}
											</ToggleButton>
										))}
									</ToggleButtonGroup>
								);
							case "custom":
								const Component = field.component;
								return <Component />;
							default:
								return null;
						}
					})()}
				</Box>
			))}

			{(() => {
				if (props?.useSaveMethod === false) return false;
				if (props?.authModelName === undefined) return true;

				// Determine if we're in add or edit mode based on presence of id in old_data
				const isEditMode = props?.old_data?.id;
				const requiredAuth = isEditMode ? `UPDATE_${props?.authModelName}` : `ADD_${props?.authModelName}`;

				return authContext.hasAuth(requiredAuth);
			})() && (
				<Button
					variant="contained"
					color="primary"
					onClick={handleSave}
					sx={{
						mt: 2,
						alignSelf: "center",
						minWidth: "100px",
						color: "#ffffff",
						...props?.saveStyle,
					}}
				>
					{props?.useAPI ? "Salva" : "Avanti"}
				</Button>
			)}
		</Box>
	) : (
		<text>Non hai le autorizzazioni per visionare questa funzionalità</text>
	);
};

export default GenericForm;
